import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaginationButtons from '../../../components/Common/Pagination';
import { UserModel } from '../../../interfaces/user.interface';
import { fetchStudents, StudentsFilters } from './services/Students.service';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import StudentsTable from './components/StudentsTable';
import StudentDetailsView from './components/StudentDetailsView';
import { TokenCookieManager } from '../../../utils/tokenCookies';
import { CreditForm } from '../Forms/CreditForm';
import AdminSearchInput from '../../../components/Admin/AdminSearchInput';
import TableInfoBar from '../../../components/Admin/TableInfoBar';
import { AdminNavHandler } from '../adminNav';

interface StudentsProps {
  onOptionClick?: AdminNavHandler;
  initialClassroomId?: string;
}

const Students = ({ onOptionClick, initialClassroomId }: StudentsProps) => {
  const [loader, setLoader] = useState(true);
  const [students, setStudents] = useState<UserModel[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  // Credit editing
  const [showCreditForm, setShowCreditForm] = useState<any | null>(null);
  const [creditDetails, setCreditDetails] = useState<number>(0);

  // Details drill-down
  const [detailsStudent, setDetailsStudent] = useState<UserModel | null>(null);

  // Filters
  const [classroomFilter, setClassroomFilter] = useState(initialClassroomId ?? '');
  const [sortBy, setSortBy] = useState('signup');
  const [sortOrder, setSortOrder] = useState('desc');

  const navigate = useNavigate();

  useEffect(() => {
    if (!TokenCookieManager.hasToken()) {
      navigate('/admin');
    }
  });

  useEffect(() => {
    if (showCreditForm === false) {
      setShowCreditForm(null);
      setCreditDetails(0);
      handleFetch();
    }
  }, [showCreditForm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (page: number | any) => {
    if (page > 0 && page <= totalPages!) setCurrentPage(+page);
  };

  const handleEditCredits = (student: UserModel) => {
    const credits = parseInt(String(student.booksCreateLimit ?? 0)) - parseInt(String(student.booksCreatedCounter ?? 0));
    setCreditDetails(isNaN(credits) ? 0 : credits);
    setShowCreditForm(student.email);
  };

  const handleClassroomClick = (classroomId: string) => {
    if (onOptionClick) {
      onOptionClick('Classrooms', { classroomId });
    }
  };

  const handleTeacherClick = (teacherEmail: string) => {
    if (onOptionClick) {
      onOptionClick('Customers', { teacherEmail });
    }
  };

  const buildFilters = useCallback((): StudentsFilters => {
    const filters: StudentsFilters = {};
    if (classroomFilter) filters.classroomId = classroomFilter;
    filters.sortBy = sortBy;
    filters.sortByValue = sortOrder;
    return filters;
  }, [classroomFilter, sortBy, sortOrder]);

  const handleFetch = async (search?: string) => {
    try {
      setLoader(true);
      const { users, totalPages: tp, totalRecords: tr } = await fetchStudents(currentPage, buildFilters(), search || searchTerm || undefined);
      setStudents(users);
      setTotalPages(tp);
      setTotalRecords(tr);
    } finally {
      setLoader(false);
    }
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
    handleFetch(term || undefined);
  };

  // Sync classroomFilter when navigated with initialClassroomId
  useEffect(() => {
    if (initialClassroomId) {
      setClassroomFilter(initialClassroomId);
      setCurrentPage(1);
    }
  }, [initialClassroomId]);

  // Fetch on mount + page change
  useEffect(() => {
    handleFetch(searchTerm || undefined);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply when filters change (debounce via setTimeout)
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      handleFetch(searchTerm || undefined);
    }, 400);
    return () => clearTimeout(timer);
  }, [classroomFilter, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (key: string) => {
    // Map column keys to server sort keys
    const sortKeyMap: Record<string, string> = {
      name: 'name',
      lastSession: 'lastSessionAt',
      created: 'signup',
    };
    const serverKey = sortKeyMap[key];
    if (!serverKey) return;
    if (sortBy === serverKey) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(serverKey);
      setSortOrder('desc');
    }
  };

  // Map server sortBy back to column key for header highlight
  const sortColumnKey = sortBy === 'name' ? 'name' : sortBy === 'lastSessionAt' ? 'lastSession' : sortBy === 'signup' ? 'created' : '';

  return (
    <div className="p-0 md:p-4">
      {detailsStudent ? (
        <StudentDetailsView
          student={detailsStudent}
          onClose={() => setDetailsStudent(null)}
        />
      ) : showCreditForm ? (
        <CreditForm
          showCreditForm={showCreditForm}
          setShowCreditForm={setShowCreditForm}
          getAllUsers={handleFetch}
          getUserBooks={async () => {}}
          creditDetails={creditDetails}
        />
      ) : (
        <>
      {/* ── Filter Bar ─────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 items-end mb-4">
        {/* Search (left) */}
        <AdminSearchInput
          placeholder="Search name, email, teacher, classroom..."
          onSearch={handleSearch}
          className="w-64"
        />

        {/* Refresh (right-aligned) */}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => handleFetch(searchTerm || undefined)}
            disabled={loader}
            className="inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded px-3 py-1.5 border border-gray-300 disabled:opacity-50 cursor-pointer"
            title="Refresh"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────── */}
      {loader ? (
        <LoadingSpinner />
      ) : (
        <>
          <StudentsTable
            students={students}
            onEditCredits={handleEditCredits}
            onViewDetails={setDetailsStudent}
            onClassroomClick={handleClassroomClick}
            onTeacherClick={handleTeacherClick}
            sortBy={sortColumnKey}
            sortDirection={sortOrder as 'asc' | 'desc'}
            onSort={handleSort}
          />
          <div className="flex justify-between items-center mt-4">
            <TableInfoBar
              currentPage={currentPage}
              pageSize={25}
              totalPages={totalPages || 0}
              totalRecords={totalRecords}
              recordsOnPage={students.length}
            />
            {(totalPages ?? 0) > 1 && (
              <PaginationButtons
                totalPages={totalPages!}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
              />
            )}
          </div>
        </>
      )}
        </>
      )}

    </div>
  );
};

export default Students;
